import React, { useMemo } from 'react'
import cx from 'classnames'
import { Field, FieldState } from 'formular'
import { useDevice } from 'device'
import { track } from 'analytics'
import { usePaymentFlow } from 'modules/payment'

import { Input } from 'components/inputs'

import RecurlyInput from './components/RecurlyInput/RecurlyInput'

import messages from './messages'


type CreditCardFormProps = {
  className?: string
  placement?: Analytics.Events['Payment form field fill in']['placement']
}

const CreditCardForm: React.FunctionComponent<CreditCardFormProps> = (props) => {
  const { className, placement = 'Page' } = props

  const { isMobile } = useDevice()
  const { creditCardForm: form } = usePaymentFlow()

  if (!form) {
    throw new Error('CreditCard form is required')
  }

  const fullCol = isMobile ? 'col-span-2' : null

  const blurHandlers = useMemo(() => {
    const getHandler = (fieldName: string, field: PaymentModule.CreditCardForm['fields'][keyof PaymentModule.CreditCardForm['fields']]) => () => {
      let error
      let isValid

      if (field instanceof Field) {
        const errorString = field.state.error?.en || field.state.error || undefined

        error = errorString === 'Required' ? 'Empty' : errorString
        isValid = field.state.isValid
      }
      else {
        // recurly element
        // @ts-ignore
        const { empty, valid } = field.state
        error = empty ? 'Empty' : (valid ? undefined : 'Invalid')
        isValid = !empty && valid
      }

      track('Payment form field fill in', {
        fieldName,
        isValid,
        error: error?.en || error,
        placement,
      })
    }

    return {
      cardNumber: getHandler('Card number', form.fields.cardNumber),
      cardMonth: getHandler('Card month', form.fields.cardMonth),
      cardYear: getHandler('Card year', form.fields.cardYear),
      cardCvv: getHandler('Card CVC', form.fields.cardCvv),
      firstName: getHandler('First name', form.fields.firstName),
      lastName: getHandler('Last name', form.fields.lastName),
      postalCode: getHandler('Postal code', form.fields.postalCode),
    }
  }, [ form, placement ])


  return (
    <div className={cx(className, 'grid grid-cols-2 gap-16')}>
      <Input
        field={form.fields.firstName}
        label={messages.firstName}
        size={48}
        pattern="A-Za-z-\s'"
        data-testid="creditCardFirstName"
        data-recurly="first_name"
        onBlur={blurHandlers.firstName}
      />
      <Input
        field={form.fields.lastName}
        label={messages.lastName}
        size={48}
        pattern="A-Za-z-\s'"
        data-testid="creditCardLastName"
        data-recurly="last_name"
        onBlur={blurHandlers.lastName}
      />
      <RecurlyInput
        className={fullCol}
        element={form.fields.cardNumber}
        label={messages.cardNumber}
        size={48}
        data-testid="creditCardNumber"
        onBlur={blurHandlers.cardNumber}
      />
      {
        isMobile ? (
          <div className="col-span-2 grid grid-cols-[84rem_84rem_auto] gap-16">
            <RecurlyInput
              element={form.fields.cardMonth}
              label={messages.month}
              size={48}
              data-testid="creditCardExpMonth"
              onBlur={blurHandlers.cardMonth}
            />
            <RecurlyInput
              element={form.fields.cardYear}
              label={messages.year}
              size={48}
              data-testid="creditCardExpYear"
              onBlur={blurHandlers.cardYear}
            />
            <RecurlyInput
              element={form.fields.cardCvv}
              label={messages.cvv}
              size={48}
              data-testid="creditCardCVC"
              onBlur={blurHandlers.cardCvv}
            />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-16">
              <RecurlyInput
                element={form.fields.cardMonth}
                label={messages.month}
                size={48}
                data-testid="creditCardExpMonth"
                onBlur={blurHandlers.cardMonth}
              />
              <RecurlyInput
                element={form.fields.cardYear}
                label={messages.year}
                size={48}
                data-testid="creditCardExpYear"
                onBlur={blurHandlers.cardYear}
              />
            </div>
            <RecurlyInput
              element={form.fields.cardCvv}
              label={messages.cvv}
              size={48}
              data-testid="creditCardCVC"
              onBlur={blurHandlers.cardCvv}
            />
          </>
        )
      }
      <div className={fullCol}>
        <Input
          field={form.fields.postalCode}
          label={messages.postalCode}
          size={48}
          data-testid="creditCardPostalCode"
          data-recurly="postal_code"
          onBlur={blurHandlers.postalCode}
        />
        <FieldState field={form.fields.country}>
          {({ value }) => (
            <input
              type="hidden"
              name="country"
              value={value}
              readOnly
              data-testid="creditCardCountry"
              data-recurly="country"
            />
          )}
        </FieldState>
      </div>
    </div>
  )
}


export default CreditCardForm
